import { clockIcon } from '@/shared/ui/assets';
import { AlertBanner } from '@/shared/ui/components';
import classNames from 'classnames';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';
import CustomRadio from './customRadio';
import { RATES_ATTRIBUTES } from './createReturnShippingRate.constants';
import type { CreateReturnShippingRateProps } from './createReturnShippingRate.types';
import './createReturnShippingRate.scss';

const CreateReturnShippingRate = ({
  isLoadedData = true,
  isMissingData = false,
  rates,
  rateId: rateIdProp,
  onChange,
}: CreateReturnShippingRateProps) => {
  /**
   * Selects a return-shipping rate from the list. Ignores the click when the rate is already the selected one, otherwise reports the id through onChange.
   *
   * @param rateId - Id of the clicked rate.
   * @returns Nothing.
   */
  const onClickItem = (rateId: any) => {
    if (rateId === rateIdProp) {
      return;
    }
    onChange(rateId);
  };

  /**
   * Translates a carrier rate's attribute flag into the label shown above its group. Maps BESTVALUE, CHEAPEST and FASTEST to their translated captions and returns null for anything else.
   *
   * @param attribute - Rate attribute constant to label.
   * @returns Group caption, or null when unlabelled.
   */
  const getAttribute = (attribute: any) => {
    switch (attribute) {
      case RATES_ATTRIBUTES.BESTVALUE: {
        return t`Best Value`;
      }
      case RATES_ATTRIBUTES.CHEAPEST: {
        return t`Cheapest`;
      }
      case RATES_ATTRIBUTES.FASTEST: {
        return t`Fastest`;
      }
      default: {
        return null;
      }
    }
  };

  /**
   * Renders the return-shipping rate list across its loading, error and loaded states. Shows three skeleton blocks until the data is loaded and a warning banner when no rates came back, otherwise buckets the rates by their first attribute, captions each bucket with the translated attribute label, and renders every rate as a radio row with carrier and service level, its estimated days singularised, and its amount to two decimals, selecting it on click.
   *
   * @returns The rate list markup or its placeholder state.
   */
  const renderList = (): any => {
    if (!isLoadedData) {
      return (
        <div className="group-skeleton">
          <Skeleton height={80} />
          <Skeleton height={80} />
          <Skeleton height={80} />
        </div>
      );
    }

    if (_.isEmpty(rates)) {
      return (
        <AlertBanner
          description={t`Something went wrong please check your information and try again.`}
          alertType="warning"
        />
      );
    }

    const groupedData: any = [];
    _.forEach(rates, (item): any => {
      const { attributes } = item;
      const index: any = groupedData.findIndex((x: any) => x.attributes === attributes?.[0]);
      if (index === -1) {
        groupedData.push({ attributes: attributes?.[0], items: [item] });
      } else {
        groupedData[index].items.push(item);
      }
    });

    return _.map(groupedData, (values, key) => (
      <div key={key} className="list">
        {values?.attributes && <div className="label">{getAttribute(values.attributes)}</div>}
        {_.map(values?.items, (value) => {
          const isActive = value?.id === rateIdProp;
          return (
            <div
              className={classNames('item', isActive && '--active')}
              onClick={() => {
                onClickItem(value?.id);
              }}
              aria-hidden
            >
              <CustomRadio {...({ checked: isActive, id: `rate-checkbox-${value?.id}` } as any)} />
              <div className="name">
                <div className="text">{`${value?.carrier} ${value?.serviceLevelName}`}</div>
                <span className="day">
                  <img alt="icon time" src={clockIcon} />
                  {value?.estimatedDays > 1
                    ? `${value?.estimatedDays} days`
                    : value?.estimatedDays !== null
                      ? `${value?.estimatedDays} day`
                      : null}
                </span>
              </div>
              <div className="corrency">{`$${parseFloat(value?.amount).toFixed(2)}`}</div>
            </div>
          );
        })}
      </div>
    ));
  };

  return (
    <div className="create-return-shipping-rate">
      <h1 className="title">{t`Rate`}</h1>
      <div className="body">
        {isMissingData && isLoadedData ? (
          <div className="empty">{t`Rate is unavailable due to missing some required information.`}</div>
        ) : (
          <div className="group">{renderList()}</div>
        )}
      </div>
    </div>
  );
};

export default CreateReturnShippingRate;
